(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory(require('./may-london-style-engine.js'));
  else root.MayLondonCapsule = factory(root.MayLondonStyleEngine);
})(typeof globalThis === 'object' ? globalThis : this, function (E) {
  const top = new Set(['tops', 'knitwear']), bottom = new Set(['trousers', 'skirts']);
  const extra = new Set(['outerwear', 'shoes', 'accessories']);
  function settings(options) {
    const value = {target:5, maxAdditions:3, budget:15000, own:[], cart:[], ...options};
    for (const [key, min, max] of [['target',1,12], ['maxAdditions',0,6], ['budget',0,10000000]]) {
      if (!Number.isSafeInteger(value[key]) || value[key] < min || value[key] > max) throw new RangeError(key);
    }
    return value;
  }
  function inventory(catalogue, filters, options) {
    const inBag = new Map(), seen = new Set();
    for (const line of options.cart) {
      if (!Number.isSafeInteger(line.quantity) || line.quantity < 0) throw new RangeError('cart');
      const id = String(line.variant_id ?? line.variantId);
      inBag.set(id, (inBag.get(id) || 0) + line.quantity);
    }
    const shop = catalogue.filter(E.validProduct).map(product => ({...product, variants:product.variants.map(variant => {
      const remaining = E.capacity(variant) - (inBag.get(String(variant.id)) || 0);
      return {...variant, available:variant.available && remaining > 0, inventory:Number.isFinite(remaining) ? remaining : variant.inventory};
    })})).filter(product => E.categories.includes(product.category) && E.matches(product, {...filters, category:'', budget:0})).filter(product => {
      if (seen.has(product.handle)) return false;
      seen.add(product.handle); return true;
    }).map(product => ({key:'shop:'+product.handle, category:product.category, price:E.price(product, filters),
      line:{kind:'shop', handle:product.handle, variantId:'', quantity:1}}));
    const owned = options.own.filter(item => item && typeof item.id === 'string' && E.categories.includes(item.category)).filter(item => {
      const key='own:'+item.id; if (seen.has(key)) return false; seen.add(key); return true;
    }).slice(0,100).map(item => ({key:'own:'+item.id, category:item.category, price:0, line:{kind:'own', id:item.id, quantity:1}}));
    shop.sort((a,b) => a.price-b.price || a.key.localeCompare(b.key));
    return {shop, owned};
  }
  function looks(items, target) {
    const bases = items.filter(item => ['dresses','sets'].includes(item.category)).map(item => [item]);
    for (const upper of items.filter(item => top.has(item.category))) {
      for (const lower of items.filter(item => bottom.has(item.category))) bases.push([upper,lower]);
    }
    const result = [], seen = new Set();
    const append = pieces => {
      const signature = pieces.map(item => item.key).sort().join('|');
      if (!seen.has(signature) && result.length < target) {
        seen.add(signature); result.push({lines:pieces.map(item => ({...item.line})), subtotal:pieces.reduce((sum,item) => sum+item.price,0)});
      }
    };
    for (const base of bases) append(base);
    for (const item of items.filter(item => extra.has(item.category))) for (const base of bases) append([...base,item]);
    return result;
  }
  function result(owned, selected, options, trim = true) {
    const combinations = looks([...owned,...selected], options.target);
    const used = new Set(combinations.flatMap(look => look.lines.filter(line => line.kind === 'shop').map(line => line.handle)));
    const additions = selected.filter(item => !trim || used.has(item.line.handle));
    return {looks:combinations, additions:additions.map(item => ({...item.line})),
      subtotal:additions.reduce((sum,item) => sum+item.price,0),
      reused:new Set(combinations.flatMap(look => look.lines.filter(line => line.kind === 'own').map(line => line.id))).size,
      complete:combinations.length >= options.target, target:options.target};
  }
  function plan(catalogue, filters = {}, input = {}) {
    const options = settings(input), {shop, owned} = inventory(catalogue, filters, options);
    // Bounded, deterministic search: reuse the wardrobe first, then maximize new looks per addition.
    const pool = E.categories.flatMap(category => shop.filter(item => item.category === category).slice(0,12));
    const groups = pool.map(item => [item]);
    for (const upper of pool.filter(item => top.has(item.category))) {
      for (const lower of pool.filter(item => bottom.has(item.category))) groups.push([upper,lower]);
    }
    const selected = []; let current = looks(owned, options.target).length, spent = 0;
    while (current < options.target && selected.length < options.maxAdditions) {
      let best;
      for (const group of groups) {
        const added = group.filter(item => !selected.includes(item));
        const cost = added.reduce((sum,item) => sum+item.price,0);
        if (!added.length || selected.length+added.length > options.maxAdditions || spent+cost > options.budget) continue;
        const count = looks([...owned,...selected,...added],options.target).length, gain = count-current;
        if (gain <= 0) continue;
        const score = gain/added.length;
        if (!best || score > best.score || score === best.score && (added.length < best.added.length || added.length === best.added.length && cost < best.cost)) best = {added,cost,count,score};
      }
      if (!best) break;
      selected.push(...best.added); spent += best.cost; current = best.count;
    }
    return result(owned,selected,options);
  }
  function selected(catalogue, filters = {}, input = {}, handles = []) {
    const options = settings(input), {shop,owned} = inventory(catalogue,filters,options);
    const chosen = shop.filter(item => handles.includes(item.line.handle));
    if (chosen.length > options.maxAdditions || chosen.reduce((sum,item) => sum+item.price,0) > options.budget) throw new RangeError('selection');
    return result(owned,chosen,options,false);
  }
  return {plan, selected};
});
